import { useRouter, useShowToast } from '@application/utils';
import type { ContactPreference } from '@gql/graphql';
import type { UnsubscribePageFormValues } from '@presentation/pages';
import { UnsubscribePage } from '@presentation/pages';
import { cloneDeep } from 'lodash/fp';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useUnsubscribe } from './api/useUnsubscribe';
import { useManageContactPreferences } from './api/useManageContactPreferences';

export const UnsubscribePublicPageController: FC = () => {
  const { showToast } = useShowToast();

  const { infoFromCurrentPath, translate, navigateToRoute } = useRouter();
  const [email] = infoFromCurrentPath.params;

  const { contactPreferencesData, contactPreferencesError, contactPreferencesLoading } =
    useUnsubscribe(email);

  const { updateContactPreferences } = useManageContactPreferences();

  useEffect(() => {
    if (contactPreferencesError) {
      showToast(translate('mt.preferences.contact.notFound'), {
        type: 'error',
        position: 'bottom-center',
      });
      navigateToRoute('homeLanding');
    }
  }, [contactPreferencesError]);

  const form = useFormik<UnsubscribePageFormValues>({
    initialValues: {
      email: contactPreferencesData?.email,
      contactPreferences: contactPreferencesData?.contactPreferences,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      await updateContactPreferences(values);
      showToast(translate('mt.preferences.contact.success'), {
        type: 'success',
        position: 'bottom-center',
      });
    },
  });
  const onChangeContactPreferenceClick = useCallback(
    (contactPreferences: ContactPreference[], value: boolean) => {
      let currentPreferences = cloneDeep(form.values?.contactPreferences ?? null);
      if (!currentPreferences) return;
      if (value) {
        currentPreferences = [...new Set(currentPreferences.concat(contactPreferences))];
      } else {
        currentPreferences = currentPreferences.filter(
          (item) => !contactPreferences.includes(item),
        );
      }
      form.setFieldValue('contactPreferences', currentPreferences);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.values?.contactPreferences],
  );

  return (
    <FormikProvider value={form}>
      <UnsubscribePage
        loading={contactPreferencesLoading}
        onChangeContactPreferenceClick={onChangeContactPreferenceClick}
        isRegisteredUser={contactPreferencesData?.isRegisteredUser ?? false}
      />
    </FormikProvider>
  );
};
